import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, ArrowRight, Mail, UserCheck, Handshake } from "lucide-react";
import useSeo from "../hooks/useSeo";
import { trackConversion } from "../lib/analytics";

const NEXT = [
  { Icon: UserCheck, t: "A recruiter reviews your profile", b: "Within 2 business days." },
  { Icon: Mail, t: "Watch your email & phone", b: "We'll reach out to confirm details — check spam just in case." },
  { Icon: Handshake, t: "Meet your employer", b: "We introduce you directly for the interview." },
];

export default function ThankYou() {
  const { state } = useLocation();
  const fired = useRef(false);
  const first = (state?.name || "").trim().split(" ")[0];

  useSeo({ title: "Application received · Direct Hire Team", description: "Your application is in. A recruiter will reach out within 2 business days.", url: "https://directhireteam.com/thank-you" });

  useEffect(() => {
    if (!state?.role || fired.current) return;
    fired.current = true;
    const key = `dht_conv_${state.appId || state.role}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    trackConversion(state.role);
  }, [state]);

  return (
    <div className="pt-28 md:pt-36 pb-20 min-h-screen bg-[#FAF7EE]" data-testid="apply-success">
      <div className="max-w-2xl mx-auto px-5 text-center">
        <div className="mx-auto h-20 w-20 rounded-full bg-[#10B981]/15 flex items-center justify-center fade-up">
          <CheckCircle2 className="h-10 w-10 text-[#0d7a55]" />
        </div>
        <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F2A1F] fade-up" style={{ animationDelay: "80ms" }} data-testid="thank-you-heading">
          You're in{first ? `, ${first}` : ""}!
        </h1>
        <p className="mt-4 text-base md:text-lg text-[#0F2A1F]/70 leading-relaxed fade-up" style={{ animationDelay: "140ms" }}>
          {state?.role ? <>Your application for <strong className="text-[#0F2A1F]">{state.role}</strong> is with our recruiters.</> : "Your application is with our recruiters."}{" "}
          {state?.tier === "high" && <span className="text-[#0d7a55] font-semibold">You're in a top-priority state — expect a fast response.</span>}
        </p>
        <ol className="mt-10 grid gap-3 text-left">
          {NEXT.map(({ Icon, t, b }, i) => (
            <li key={t} className="flex items-start gap-4 rounded-2xl bg-white p-5 ring-1 ring-[#0F2A1F]/10 fade-up" style={{ animationDelay: `${200 + i * 80}ms` }}>
              <div className="h-10 w-10 shrink-0 rounded-xl bg-[#0F2A1F] text-[#D4AF37] flex items-center justify-center"><Icon className="h-5 w-5" /></div>
              <div><div className="font-bold text-[#0F2A1F]">{t}</div><div className="text-sm text-[#0F2A1F]/60">{b}</div></div>
            </li>
          ))}
        </ol>
        <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
          <Link to="/jobs" className="h-12 px-6 rounded-xl bg-[#0F2A1F] text-white text-sm font-semibold inline-flex items-center justify-center gap-2 hover:bg-[#163b2c]" data-testid="success-back-jobs">Apply to more roles <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/enroll" className="h-12 px-6 rounded-xl ring-1 ring-[#0F2A1F]/20 text-sm font-semibold text-[#0F2A1F] inline-flex items-center justify-center hover:ring-[#0F2A1F]/40" data-testid="success-prc-cta">Boost your profile with a free certificate</Link>
        </div>
      </div>
    </div>
  );
}
